import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    DatePicker,
    Drawer,
    Empty,
    Input,
    InputNumber,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
    Upload,
    message
} from 'antd';
import {
    CameraOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloudDownloadOutlined,
    EnvironmentOutlined,
    FilterOutlined,
    ReloadOutlined,
    RollbackOutlined,
    ToolOutlined
} from '@ant-design/icons';
import '../styles/InstallerWorkHub.css';
import {
    enqueueReportPhoto,
    enqueueSms
} from '../utils/installerOfflineQueue';
import { apiUrl } from '../utils/apiConfig';
import {
    loadCachedReport,
    loadCachedWork,
    saveCachedReport,
    saveCachedWork
} from '../utils/installerTaskCache';
import { isInstallerNativeApp } from '../utils/installerSms';
import {
    bufferPhotoSrc,
    bufferPhotoToFile,
    listBufferedPhotos,
    removeBufferedPhoto,
    subscribePhotoBuffer
} from '../utils/installerPhotoBuffer';
import {
    buildInstallerSmsCodes,
    forgetActiveTask,
    rememberActiveTask,
    smsTextForAction,
    syncActiveTasksFromList
} from '../utils/installerWoSms';
import InstallerTaskFields from './InstallerTaskFields';

const NEW_INSTALLER_URL = 'https://task.smartcoms.ru/task-manager/installer';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const STAGE_COLORS = {
    новое: '#999999',
    назначено: '#33ccff',
    'принято исполнителем': '#6666FF',
    'в дороге на объект': '#009999',
    'на объекте': '#d4b106',
    'работы завершены': '#ff9933',
    'выполнено (заполнен отчет)': '#33cc33',
    'выполнено (заполнен отчёт)': '#33cc33',
    'возращено исполнителю': '#ff9933',
    'возвращено исполнителю': '#ff9933',
    осмечено: '#FF1493'
};

const LIST_CHIPS = [
    { key: 'active', label: 'В работе' },
    { key: 'all', label: 'Все' },
    { key: 'unfinished', label: 'Незавершённые' },
    { key: 'done', label: 'С отчётом' }
];

const normalizeStage = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е');

const isActiveStage = status => {
    const s = normalizeStage(status);
    return [
        'новое',
        'назначено',
        'принято исполнителем',
        'в дороге на объект',
        'на объекте',
        'работы завершены',
        'возвращено исполнителю',
        'возращено исполнителю'
    ].includes(s);
};

const StageTag = ({ status }) => {
    const key = normalizeStage(status);
    const color =
        STAGE_COLORS[key] ||
        STAGE_COLORS[status] ||
        '#1677ff';
    return (
        <Tag
            style={{
                margin: 0,
                border: 'none',
                background: color,
                color: '#141414',
                maxWidth: '100%',
                whiteSpace: 'normal'
            }}
        >
            {status || '—'}
        </Tag>
    );
};

const InstallerWorkHub = () => {
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [chip, setChip] = useState('active');
    const [filters, setFilters] = useState({
        search: '',
        ttNumber: undefined,
        workType: undefined,
        status: undefined
    });
    const [report, setReport] = useState({
        tasks: [],
        unfinished: [],
        options: {}
    });

    const [taskOpen, setTaskOpen] = useState(false);
    const [taskLoading, setTaskLoading] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [work, setWork] = useState(null);
    const [description, setDescription] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [installRow, setInstallRow] = useState(null);
    const [installQty, setInstallQty] = useState(1);
    const [dismantleRow, setDismantleRow] = useState(null);
    const [dismantleQty, setDismantleQty] = useState(1);
    const [dismantleHint, setDismantleHint] = useState(null);
    const [bufferPhotos, setBufferPhotos] = useState([]);
    const [fromCache, setFromCache] = useState(false);
    const [caching, setCaching] = useState(false);
    const [photoMarker, setPhotoMarker] = useState('otchet');
    const [fieldValues, setFieldValues] = useState({});
    const [fieldsSaving, setFieldsSaving] = useState(false);

    const dateParams = useMemo(
        () => ({
            dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
            dateTo: dateRange?.[1]?.format('YYYY-MM-DD')
        }),
        [dateRange]
    );

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                '/api/installer/work/my-tasks',
                {
                    headers: authHeaders(),
                    params: {
                        dateFrom: dateParams.dateFrom,
                        dateTo: dateParams.dateTo,
                        search: filters.search || undefined,
                        status: filters.status
                    }
                }
            );
            const next = {
                tasks: data?.data?.tasks || data?.tasks || [],
                unfinished:
                    data?.data?.unfinished ||
                    data?.unfinished ||
                    [],
                options:
                    data?.data?.options || data?.options || {}
            };
            setReport(next);
            setFromCache(false);
            syncActiveTasksFromList(next.tasks || []);
            saveCachedReport({
                dateFrom: dateParams.dateFrom,
                dateTo: dateParams.dateTo,
                report: next
            }).catch(() => {});
            return next;
        } catch (error) {
            const cached = await loadCachedReport({
                dateFrom: dateParams.dateFrom,
                dateTo: dateParams.dateTo
            });
            if (cached?.report) {
                setReport(cached.report);
                setFromCache(true);
                syncActiveTasksFromList(cached.report.tasks || []);
                message.warning(
                    'Нет сети — показаны сохранённые заявки'
                );
                return cached.report;
            }
            message.error(
                error.response?.data?.message ||
                    'Не удалось загрузить заявки'
            );
            return null;
        } finally {
            setLoading(false);
        }
    }, [dateParams, filters]);

    useEffect(() => {
        fetchReport();
    }, [
        dateRange,
        filters.ttNumber,
        filters.workType,
        filters.status
    ]);

    const visibleTasks = useMemo(() => {
        let list = report.tasks || [];
        if (chip === 'unfinished') {
            list = report.unfinished || [];
        } else if (chip === 'active') {
            list = list.filter(t => isActiveStage(t.status));
        } else if (chip === 'done') {
            list = list.filter(t =>
                normalizeStage(t.status).includes(
                    'заполнен отчет'
                )
            );
        }
        if (filters.search) {
            const q = filters.search.toLowerCase();
            list = list.filter(t =>
                [
                    t.title,
                    t.externalId,
                    t.ttNumber,
                    t.bsNumber,
                    t.workType,
                    t.status
                ]
                    .join(' ')
                    .toLowerCase()
                    .includes(q)
            );
        }
        return list;
    }, [report, chip, filters.search]);

    const loadWork = async task => {
        if (!task?.id) {
            message.warning('Нет внутреннего ID заявки');
            return;
        }
        setSelectedTask(task);
        setTaskOpen(true);
        setTaskLoading(true);
        setWork(null);
        setDismantleRow(null);
        setDismantleHint(null);
        window.dispatchEvent(
            new CustomEvent('installer-task-sheet', {
                detail: { open: true, taskId: task.id }
            })
        );
        try {
            const { data } = await axios.get(
                `/api/installer/work/${task.id}`,
                { headers: authHeaders() }
            );
            const bundle = data.data;
            setWork(bundle);
            setDescription(
                bundle?.report?.description || ''
            );
            const nextValues = {};
            (bundle?.task?.fields || []).forEach(field => {
                nextValues[field.system_name] = field.value;
            });
            setFieldValues(nextValues);
            saveCachedWork(task.id, bundle, task).catch(() => {});
            listBufferedPhotos(task.id)
                .then(setBufferPhotos)
                .catch(() => setBufferPhotos([]));
        } catch (error) {
            const cached = await loadCachedWork(task.id);
            if (cached?.bundle) {
                setWork(cached.bundle);
                setDescription(
                    cached.bundle?.report?.description || ''
                );
                const nextValues = {};
                (cached.bundle?.task?.fields || []).forEach(
                    field => {
                        nextValues[field.system_name] =
                            field.value;
                    }
                );
                setFieldValues(nextValues);
                message.warning(
                    'Заявка из кэша — без сети детали могут быть неполными'
                );
            } else {
                message.error(
                    error.response?.data?.message ||
                        'Не удалось открыть заявку'
                );
            }
        } finally {
            setTaskLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedTask?.id) {
            return undefined;
        }
        return subscribePhotoBuffer(rows => {
            setBufferPhotos(
                (rows || []).filter(
                    row =>
                        Number(row.taskId) ===
                        Number(selectedTask.id)
                )
            );
        });
    }, [selectedTask?.id]);

    const closeTaskSheet = () => {
        setTaskOpen(false);
        setInstallRow(null);
        setDismantleRow(null);
        setFieldValues({});
        window.dispatchEvent(
            new CustomEvent('installer-task-sheet', {
                detail: { open: false }
            })
        );
    };

    const saveTaskFields = async () => {
        if (!selectedTask?.id || !work?.task?.fields) return;
        const updates = {};
        work.task.fields.forEach(field => {
            if (!field.can_edit) return;
            const next = fieldValues[field.system_name];
            if (next !== field.value) {
                updates[field.system_name] = next;
            }
        });
        if (!Object.keys(updates).length) {
            return;
        }
        setFieldsSaving(true);
        try {
            await axios.put(
                `/api/installer/work/${selectedTask.id}/fields`,
                { fields: updates },
                { headers: authHeaders() }
            );
            message.success('Поля сохранены');
            await loadWork(selectedTask);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить поля'
            );
        } finally {
            setFieldsSaving(false);
        }
    };

    const runAction = async action => {
        if (!selectedTask?.id) return;
        setActionLoading(true);
        try {
            const { data } = await axios.post(
                `/api/installer/work/${selectedTask.id}/status`,
                { action },
                { headers: authHeaders() }
            );
            const codes = buildInstallerSmsCodes({
                externalId:
                    work?.task?.external_id ||
                    selectedTask.externalId,
                ttNumber: selectedTask.ttNumber,
                smsCodes: work?.sms_codes
            });
            const smsText = smsTextForAction(action, codes);
            const needsSms = [
                'accept',
                'on_site',
                'cancel'
            ].includes(action);

            if (needsSms && !smsText) {
                message.warning(
                    'Нет номера WO в заявке — SMS на 359 не сформирован'
                );
            }

            if (smsText && isInstallerNativeApp()) {
                try {
                    const sms = await enqueueSms(smsText);
                    message.success(
                        sms.queued
                            ? `Статус: ${data.data.to_status}. SMS в очереди: ${smsText}`
                            : `Статус: ${data.data.to_status}. SMS 359: ${smsText}`
                    );
                } catch (smsError) {
                    message.warning(
                        `Статус сменён, SMS не ушло: ${
                            smsError?.message || smsText
                        }`
                    );
                }
            } else {
                message.success(
                    `Статус: ${data.data.to_status}`
                );
                if (smsText && !isInstallerNativeApp()) {
                    message.info(`Код для 359: ${smsText}`);
                }
            }

            if (action === 'accept') {
                rememberActiveTask({
                    ...selectedTask,
                    status: data.data.to_status
                });
            }
            if (action === 'cancel') {
                forgetActiveTask(selectedTask.id);
            }

            await loadWork(selectedTask);
            fetchReport();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сменить статус'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const saveDraft = async () => {
        if (!selectedTask?.id) return;
        try {
            await axios.put(
                `/api/installer/work/${selectedTask.id}/report`,
                { description },
                { headers: authHeaders() }
            );
            message.success('Черновик сохранён');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить'
            );
        }
    };

    const submitReport = async () => {
        if (!selectedTask?.id) return;
        setActionLoading(true);
        try {
            await axios.post(
                `/api/installer/work/${selectedTask.id}/report/submit`,
                { description },
                { headers: authHeaders() }
            );
            message.success(
                'Отчёт сдан. ТМЦ на объекте списано.'
            );
            forgetActiveTask(selectedTask.id);
            await loadWork(selectedTask);
            fetchReport();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось сдать отчёт'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const uploadPhoto = async file => {
        if (!selectedTask?.id) return false;
        try {
            const result = await enqueueReportPhoto({
                taskId: selectedTask.id,
                file,
                marker: photoMarker || 'otchet'
            });
            if (result.queued) {
                message.warning(
                    'Нет сети — фото в очереди, загрузится при появлении интернета'
                );
            } else {
                setWork(prev =>
                    prev
                        ? {
                            ...prev,
                            report: result.data?.data
                        }
                        : prev
                );
                message.success('Фото добавлено');
                await loadWork(selectedTask);
            }
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка загрузки фото'
            );
        }
        return false;
    };

    const confirmInstall = async () => {
        if (!selectedTask?.id || !installRow) return;
        setActionLoading(true);
        try {
            await axios.post(
                `/api/installer/work/${selectedTask.id}/tmc`,
                {
                    holding_id: installRow.id,
                    quantity: installQty
                },
                { headers: authHeaders() }
            );
            message.success(
                'ТМЦ перенесено на объект (пока работы не закрыты — числится на вас)'
            );
            setInstallRow(null);
            await loadWork(selectedTask);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось установить ТМЦ'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const confirmDismantle = async () => {
        if (!selectedTask?.id) return;
        if (!dismantleRow && !dismantleHint) {
            message.warning('Выберите, что сняли');
            return;
        }
        setActionLoading(true);
        try {
            await axios.post(
                `/api/installer/work/${selectedTask.id}/tmc/dismantle`,
                {
                    holding_id: dismantleRow?.id || null,
                    quantity: dismantleQty,
                    hint_name:
                        dismantleHint?.name ||
                        dismantleRow?.item?.name ||
                        null
                },
                { headers: authHeaders() }
            );
            message.success('Демонтаж записан');
            setDismantleRow(null);
            setDismantleHint(null);
            await loadWork(selectedTask);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось зафиксировать демонтаж'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const pullBufferIntoReport = async (photo = null) => {
        if (!selectedTask?.id) return;
        const list = photo ? [photo] : bufferPhotos;
        if (!list.length) return;
        setActionLoading(true);
        try {
            let queued = 0;
            let sent = 0;
            for (const item of list) {
                const file = bufferPhotoToFile(item);
                const result = await enqueueReportPhoto({
                    taskId: selectedTask.id,
                    file
                });
                if (result.queued) queued += 1;
                else sent += 1;
                await removeBufferedPhoto(item.id);
            }
            if (queued) {
                message.warning(
                    `В отчёт из буфера: ${sent}, в очереди: ${queued}`
                );
            } else {
                message.success(
                    `В фотоотчёт добавлено: ${sent}`
                );
            }
            await loadWork(selectedTask);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось перенести фото из буфера'
            );
        } finally {
            setActionLoading(false);
        }
    };

    const photoUrl = photo =>
        apiUrl(
            `/api/installer/work/photo?path=${encodeURIComponent(
                photo.path
            )}&token=${encodeURIComponent(
                localStorage.getItem('token') || ''
            )}`
        );

    const reportSubmitted =
        work?.report?.status === 'submitted';

    const smsCodes = useMemo(
        () =>
            buildInstallerSmsCodes({
                externalId:
                    work?.task?.external_id ||
                    selectedTask?.externalId,
                ttNumber: selectedTask?.ttNumber,
                smsCodes: work?.sms_codes
            }),
        [
            work?.sms_codes,
            work?.task?.external_id,
            selectedTask?.externalId,
            selectedTask?.ttNumber
        ]
    );

    const cacheTasksOffline = async () => {
        setCaching(true);
        try {
            const current = await fetchReport();
            const list = (current?.tasks || []).filter(t => t?.id);
            let saved = 0;
            for (const task of list.slice(0, 40)) {
                try {
                    const { data } = await axios.get(
                        `/api/installer/work/${task.id}`,
                        { headers: authHeaders() }
                    );
                    await saveCachedWork(task.id, data.data, task);
                    saved += 1;
                } catch (_) {
                    /* skip one task */
                }
            }
            if (saved || (current?.tasks || []).length) {
                message.success(
                    `Сохранено для офлайна: список и ${saved} заявок`
                );
            } else {
                message.warning(
                    'Нечего сохранить — нужен интернет'
                );
            }
        } finally {
            setCaching(false);
        }
    };

    return (
        <div className="installer-work-hub">
            <div className="installer-work-hub__header">
                <div>
                    <Title
                        level={3}
                        className="installer-work-hub__title"
                    >
                        Мои заявки
                    </Title>
                    <Text
                        type="secondary"
                        className="installer-work-hub__subtitle"
                    >
                        Приём, объект, отчёт и ТМЦ
                    </Text>
                </div>
                <Space>
                    <Button
                        icon={<CloudDownloadOutlined />}
                        onClick={cacheTasksOffline}
                        loading={caching}
                        title="Сохранить заявки на телефон"
                    >
                        В кэш
                    </Button>
                    <Button
                        icon={<FilterOutlined />}
                        onClick={() => setFiltersOpen(true)}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchReport}
                        loading={loading}
                    />
                </Space>
            </div>

            <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Этот кабинет монтажника закрывается"
                description="Работайте в новом приложении: статусы, отчёт и SMS там идут по маршрутам воронки. Вход тем же логином и паролем."
                action={
                    <Button
                        type="primary"
                        href={NEW_INSTALLER_URL}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Открыть
                    </Button>
                }
            />

            {fromCache ? (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Показаны сохранённые заявки"
                    description="Нет связи с сервером. Нажмите «В кэш», когда будет интернет — заявки снова сохранятся на телефон."
                />
            ) : null}

            <div className="installer-work-hub__chips">
                {LIST_CHIPS.map(item => (
                    <button
                        key={item.key}
                        type="button"
                        className={`installer-work-hub__chip${
                            chip === item.key
                                ? ' is-active'
                                : ''
                        }`}
                        onClick={() => setChip(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <Input.Search
                allowClear
                placeholder="Поиск: ТТ, адрес, статус…"
                value={filters.search}
                onChange={e =>
                    setFilters(f => ({
                        ...f,
                        search: e.target.value
                    }))
                }
                onSearch={fetchReport}
                style={{ marginBottom: 12 }}
            />

            <Spin spinning={loading}>
                {visibleTasks.length ? (
                    visibleTasks.map(task => (
                        <div
                            key={task.id || task.externalId}
                            className="installer-work-card"
                            onClick={() => loadWork(task)}
                        >
                            <div className="installer-work-card__top">
                                <div className="installer-work-card__title">
                                    {task.title ||
                                        `Заявка ${
                                            task.externalId ||
                                            task.id
                                        }`}
                                </div>
                                <StageTag
                                    status={task.status}
                                />
                            </div>
                            <div className="installer-work-card__meta">
                                <div>
                                    <b>ТТ</b>{' '}
                                    {task.ttNumber || '—'}
                                    {task.bsNumber
                                        ? ` · БС ${task.bsNumber}`
                                        : ''}
                                </div>
                                <div>
                                    <b>Работы</b>{' '}
                                    {task.workType || '—'}
                                </div>
                                {(task.appearanceAt ||
                                    task.leaveAt) && (
                                    <div>
                                        <b>На объекте</b>{' '}
                                        {task.appearanceAt
                                            ? dayjs(
                                                  task.appearanceAt
                                              ).format(
                                                  'DD.MM HH:mm'
                                              )
                                            : '—'}
                                        {' → '}
                                        {task.leaveAt
                                            ? dayjs(
                                                  task.leaveAt
                                              ).format(
                                                  'DD.MM HH:mm'
                                              )
                                            : '—'}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <Empty description="Нет заявок по фильтру" />
                )}
            </Spin>

            <Drawer
                title="Фильтры"
                placement="bottom"
                height="70%"
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
            >
                <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size={12}
                >
                    <div>
                        <Text type="secondary">Период</Text>
                        <RangePicker
                            value={dateRange}
                            format="DD.MM.YYYY"
                            style={{
                                width: '100%',
                                marginTop: 6
                            }}
                            onChange={value =>
                                setDateRange(
                                    value || [
                                        dayjs().startOf(
                                            'month'
                                        ),
                                        dayjs().endOf('month')
                                    ]
                                )
                            }
                        />
                    </div>
                    <Select
                        allowClear
                        showSearch
                        placeholder="Номер ТТ"
                        style={{ width: '100%' }}
                        value={filters.ttNumber}
                        options={(
                            report.options?.ttNumbers || []
                        ).map(v => ({ label: v, value: v }))}
                        onChange={v =>
                            setFilters(f => ({
                                ...f,
                                ttNumber: v
                            }))
                        }
                    />
                    <Select
                        allowClear
                        showSearch
                        placeholder="Вид работ"
                        style={{ width: '100%' }}
                        value={filters.workType}
                        options={(
                            report.options?.workTypes || []
                        ).map(v => ({ label: v, value: v }))}
                        onChange={v =>
                            setFilters(f => ({
                                ...f,
                                workType: v
                            }))
                        }
                    />
                    <Select
                        allowClear
                        showSearch
                        placeholder="Статус"
                        style={{ width: '100%' }}
                        value={filters.status}
                        options={(
                            report.options?.statuses || []
                        ).map(v => ({ label: v, value: v }))}
                        onChange={v =>
                            setFilters(f => ({
                                ...f,
                                status: v
                            }))
                        }
                    />
                    <Button
                        block
                        onClick={() => {
                            setFilters({
                                search: '',
                                ttNumber: undefined,
                                workType: undefined,
                                status: undefined
                            });
                            setDateRange([
                                dayjs().startOf('month'),
                                dayjs().endOf('month')
                            ]);
                        }}
                    >
                        Сбросить
                    </Button>
                    <Button
                        type="primary"
                        block
                        onClick={() => {
                            setFiltersOpen(false);
                            fetchReport();
                        }}
                    >
                        Применить
                    </Button>
                </Space>
            </Drawer>

            <Drawer
                className="installer-task-sheet"
                title={
                    selectedTask?.ttNumber
                        ? `ТТ ${selectedTask.ttNumber}`
                        : 'Заявка'
                }
                placement="bottom"
                height="92%"
                open={taskOpen}
                onClose={closeTaskSheet}
                destroyOnClose
                zIndex={1200}
                getContainer={() => document.body}
                styles={{
                    body: {
                        paddingBottom: 32,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch'
                    }
                }}
            >
                <Spin spinning={taskLoading}>
                    {work ? (
                        <>
                            <div className="installer-task-sheet__hero">
                                <Space
                                    direction="vertical"
                                    size={8}
                                    style={{ width: '100%' }}
                                >
                                    <Text strong>
                                        {work.task?.title ||
                                            selectedTask?.title}
                                    </Text>
                                    <StageTag
                                        status={
                                            work.task
                                                ?.status
                                        }
                                    />
                                    <Text type="secondary">
                                        ID{' '}
                                        {work.task
                                            ?.external_id ||
                                            '—'}
                                    </Text>
                                    {work.task?.customer ? (
                                        <Text type="secondary">
                                            Заказчик:{' '}
                                            {work.task.customer}
                                        </Text>
                                    ) : null}
                                    {work.task?.address ? (
                                        <Text type="secondary">
                                            {work.task.address}
                                        </Text>
                                    ) : null}
                                </Space>
                            </div>

                            <InstallerTaskFields
                                fields={work.task?.fields || []}
                                values={fieldValues}
                                reportMode={work.can_fill_report}
                                saving={fieldsSaving}
                                onChange={(name, value) =>
                                    setFieldValues(prev => ({
                                        ...prev,
                                        [name]: value
                                    }))
                                }
                                onSave={saveTaskFields}
                            />

                            {!reportSubmitted &&
                                (work.available_actions
                                    ?.length > 0 ||
                                    work.can_fill_report) && (
                                    <div className="installer-task-sheet__actions">
                                        {work.available_actions.map(
                                            item => (
                                                <Button
                                                    key={
                                                        item.action
                                                    }
                                                    type={
                                                        item.action ===
                                                        'cancel'
                                                            ? 'default'
                                                            : 'primary'
                                                    }
                                                    danger={
                                                        item.action ===
                                                        'cancel'
                                                    }
                                                    icon={
                                                        item.action ===
                                                        'accept' ? (
                                                            <CheckCircleOutlined />
                                                        ) : item.action ===
                                                          'on_site' ? (
                                                            <EnvironmentOutlined />
                                                        ) : item.action ===
                                                          'cancel' ? (
                                                            <CloseCircleOutlined />
                                                        ) : (
                                                            <ToolOutlined />
                                                        )
                                                    }
                                                    loading={
                                                        actionLoading
                                                    }
                                                    onClick={() =>
                                                        runAction(
                                                            item.action
                                                        )
                                                    }
                                                >
                                                    {
                                                        item.label
                                                    }
                                                </Button>
                                            )
                                        )}
                                    </div>
                                )}

                            {smsCodes.wo ? (
                                <Text
                                    type="secondary"
                                    style={{
                                        display: 'block',
                                        margin: '-4px 0 12px'
                                    }}
                                >
                                    SMS 359: приём {smsCodes.accept}
                                </Text>
                            ) : null}

                            {(reportSubmitted ||
                                work.can_fill_report) && (
                            <div className="installer-report-block">
                                <Title
                                    level={5}
                                    style={{
                                        marginTop: 0
                                    }}
                                >
                                    Отчёт по работам
                                </Title>
                                {reportSubmitted ? (
                                    <Alert
                                        type="success"
                                        showIcon
                                        message="Отчёт сдан"
                                        description={
                                            work.report
                                                ?.description
                                        }
                                    />
                                ) : (
                                    <>
                                        <TextArea
                                            rows={4}
                                            placeholder="Описание выполненных работ…"
                                            value={
                                                description
                                            }
                                            onChange={e =>
                                                setDescription(
                                                    e.target
                                                        .value
                                                )
                                            }
                                        />
                                        <Space
                                            style={{
                                                marginTop: 10,
                                                width: '100%'
                                            }}
                                            wrap
                                        >
                                            <Select
                                                value={photoMarker}
                                                style={{
                                                    minWidth: 140
                                                }}
                                                options={(
                                                    work.photo_markers ||
                                                    [
                                                        {
                                                            value: 'montazh',
                                                            label: 'Монтаж'
                                                        },
                                                        {
                                                            value: 'demontazh',
                                                            label: 'Демонтаж'
                                                        },
                                                        {
                                                            value: 'otchet',
                                                            label: 'Отчет'
                                                        }
                                                    ]
                                                ).map(item => ({
                                                    value: item.value,
                                                    label: item.label
                                                }))}
                                                onChange={setPhotoMarker}
                                            />
                                            <Upload
                                                accept="image/*"
                                                capture="environment"
                                                showUploadList={
                                                    false
                                                }
                                                beforeUpload={
                                                    uploadPhoto
                                                }
                                            >
                                                <Button
                                                    icon={
                                                        <CameraOutlined />
                                                    }
                                                >
                                                    Фото
                                                </Button>
                                            </Upload>
                                            <Button
                                                onClick={
                                                    saveDraft
                                                }
                                            >
                                                Черновик
                                            </Button>
                                            <Button
                                                type="primary"
                                                loading={
                                                    actionLoading
                                                }
                                                onClick={
                                                    submitReport
                                                }
                                            >
                                                Сдать отчёт
                                            </Button>
                                        </Space>
                                    </>
                                )}

                                {!!work.report?.photos
                                    ?.length && (
                                    <div className="installer-photo-grid">
                                        {work.report.photos.map(
                                            (
                                                photo,
                                                idx
                                            ) => (
                                                <img
                                                    key={
                                                        photo.path ||
                                                        idx
                                                    }
                                                    src={photoUrl(
                                                        photo
                                                    )}
                                                    alt={
                                                        photo.name ||
                                                        'фото'
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                )}

                                {!reportSubmitted &&
                                    bufferPhotos.length >
                                        0 && (
                                        <div
                                            style={{
                                                marginTop: 12
                                            }}
                                        >
                                            <Space
                                                style={{
                                                    width: '100%',
                                                    justifyContent:
                                                        'space-between'
                                                }}
                                            >
                                                <Text strong>
                                                    Буфер по
                                                    дороге
                                                </Text>
                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    loading={
                                                        actionLoading
                                                    }
                                                    onClick={() =>
                                                        pullBufferIntoReport()
                                                    }
                                                >
                                                    В
                                                    фотоотчёт
                                                </Button>
                                            </Space>
                                            <div className="installer-photo-grid">
                                                {bufferPhotos.map(
                                                    photo => (
                                                        <div
                                                            key={
                                                                photo.id
                                                            }
                                                            className="installer-buffer-card"
                                                        >
                                                            <img
                                                                src={bufferPhotoSrc(
                                                                    photo
                                                                )}
                                                                alt="буфер"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    pullBufferIntoReport(
                                                                        photo
                                                                    )
                                                                }
                                                            >
                                                                в
                                                                отчёт
                                                            </button>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                            </div>
                            )}

                            {!reportSubmitted && (
                                    <div className="installer-report-block">
                                        <Title
                                            level={5}
                                            style={{
                                                marginTop: 0
                                            }}
                                        >
                                            ТМЦ на объект
                                        </Title>
                                        <Text
                                            type="secondary"
                                            style={{
                                                display:
                                                    'block',
                                                marginBottom: 8
                                            }}
                                        >
                                            Выберите из
                                            наличия. Демонтаж
                                            — кнопка «Снять»
                                            и подсказки из
                                            заявки, что
                                            снимали.
                                        </Text>

                                        {(work.dismantle_hints ||
                                            [])
                                            .length > 0 && (
                                            <div
                                                style={{
                                                    marginBottom: 10
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        display:
                                                            'block',
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    Подсказки
                                                    из заявки
                                                </Text>
                                                <Space
                                                    wrap
                                                    size={6}
                                                >
                                                    {(
                                                        work.dismantle_hints ||
                                                        []
                                                    ).map(
                                                        hint => (
                                                            <button
                                                                key={
                                                                    hint.id
                                                                }
                                                                type="button"
                                                                className={`installer-hint-chip${
                                                                    dismantleHint?.id ===
                                                                    hint.id
                                                                        ? ' is-active'
                                                                        : ''
                                                                }`}
                                                                onClick={() => {
                                                                    setDismantleHint(
                                                                        hint
                                                                    );
                                                                    const match =
                                                                        (
                                                                            work.on_object_tmc ||
                                                                            []
                                                                        ).find(
                                                                            row =>
                                                                                String(
                                                                                    row
                                                                                        .item
                                                                                        ?.name ||
                                                                                        ''
                                                                                )
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                        String(
                                                                                            hint.name ||
                                                                                                ''
                                                                                        )
                                                                                            .toLowerCase()
                                                                                            .slice(
                                                                                                0,
                                                                                                18
                                                                                            )
                                                                                    )
                                                                        );
                                                                    if (
                                                                        match
                                                                    ) {
                                                                        setDismantleRow(
                                                                            match
                                                                        );
                                                                        setDismantleQty(
                                                                            Number(
                                                                                match.quantity
                                                                            )
                                                                        );
                                                                    } else {
                                                                        setDismantleQty(
                                                                            Number(
                                                                                hint.quantity ||
                                                                                    1
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {
                                                                    hint.name
                                                                }
                                                            </button>
                                                        )
                                                    )}
                                                </Space>
                                            </div>
                                        )}

                                        {(work.on_object_tmc ||
                                            [])
                                            .length > 0 && (
                                            <>
                                                <Text strong>
                                                    Уже на
                                                    объекте
                                                </Text>
                                                {(
                                                    work.on_object_tmc ||
                                                    []
                                                ).map(
                                                    row => (
                                                        <div
                                                            key={
                                                                row.id
                                                            }
                                                            className="installer-tmc-row"
                                                        >
                                                            <div>
                                                                {row
                                                                    .item
                                                                    ?.name ||
                                                                    '—'}
                                                                <div>
                                                                    <Text type="secondary">
                                                                        {
                                                                            row.quantity
                                                                        }{' '}
                                                                        {row
                                                                            .item
                                                                            ?.unit ||
                                                                            ''}
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                            {dismantleRow?.id ===
                                                            row.id ? (
                                                                <Space>
                                                                    <InputNumber
                                                                        min={
                                                                            0.001
                                                                        }
                                                                        max={Number(
                                                                            row.quantity
                                                                        )}
                                                                        value={
                                                                            dismantleQty
                                                                        }
                                                                        onChange={
                                                                            setDismantleQty
                                                                        }
                                                                        style={{
                                                                            width: 80
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        loading={
                                                                            actionLoading
                                                                        }
                                                                        onClick={
                                                                            confirmDismantle
                                                                        }
                                                                    >
                                                                        OK
                                                                    </Button>
                                                                </Space>
                                                            ) : (
                                                                <Button
                                                                    size="small"
                                                                    icon={
                                                                        <RollbackOutlined />
                                                                    }
                                                                    onClick={() => {
                                                                        setDismantleRow(
                                                                            row
                                                                        );
                                                                        setDismantleQty(
                                                                            Number(
                                                                                row.quantity
                                                                            )
                                                                        );
                                                                    }}
                                                                >
                                                                    Снять
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                            </>
                                        )}

                                        {dismantleHint &&
                                            !dismantleRow && (
                                                <Button
                                                    block
                                                    icon={
                                                        <RollbackOutlined />
                                                    }
                                                    loading={
                                                        actionLoading
                                                    }
                                                    onClick={
                                                        confirmDismantle
                                                    }
                                                    style={{
                                                        margin:
                                                            '8px 0'
                                                    }}
                                                >
                                                    Записать
                                                    демонтаж:{' '}
                                                    {
                                                        dismantleHint.name
                                                    }
                                                </Button>
                                            )}

                                        {(work.held_tmc || [])
                                            .length ? (
                                            (
                                                work.held_tmc ||
                                                []
                                            ).map(row => (
                                                <div
                                                    key={
                                                        row.id
                                                    }
                                                    className="installer-tmc-row"
                                                >
                                                    <div>
                                                        {row
                                                            .item
                                                            ?.name ||
                                                            '—'}
                                                        <div>
                                                            <Text type="secondary">
                                                                Доступно:{' '}
                                                                {
                                                                    row.quantity
                                                                }{' '}
                                                                {row
                                                                    .item
                                                                    ?.unit ||
                                                                    ''}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    {installRow?.id ===
                                                    row.id ? (
                                                        <Space>
                                                            <InputNumber
                                                                min={
                                                                    0.001
                                                                }
                                                                max={Number(
                                                                    row.quantity
                                                                )}
                                                                value={
                                                                    installQty
                                                                }
                                                                onChange={
                                                                    setInstallQty
                                                                }
                                                                style={{
                                                                    width: 80
                                                                }}
                                                            />
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                loading={
                                                                    actionLoading
                                                                }
                                                                onClick={
                                                                    confirmInstall
                                                                }
                                                            >
                                                                OK
                                                            </Button>
                                                        </Space>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setInstallRow(
                                                                    row
                                                                );
                                                                setInstallQty(
                                                                    Number(
                                                                        row.quantity
                                                                    )
                                                                );
                                                            }}
                                                        >
                                                            Установить
                                                        </Button>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <Empty
                                                image={
                                                    Empty
                                                        .PRESENTED_IMAGE_SIMPLE
                                                }
                                                description="Нет ТМЦ в наличии"
                                            />
                                        )}
                                    </div>
                                )}

                            {(work.on_object_tmc || [])
                                .length > 0 &&
                                reportSubmitted && (
                                    <Alert
                                        type="success"
                                        showIcon
                                        message="ТМЦ по этой заявке списано при сдаче отчёта"
                                    />
                                )}
                        </>
                    ) : (
                        !taskLoading && (
                            <Empty description="Нет данных" />
                        )
                    )}
                </Spin>
            </Drawer>
        </div>
    );
};

export default InstallerWorkHub;
